import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Paper,
  Popover,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { apiGetSettings, apiCreateSetting, apiUpdateSetting } from "../../../graphQL/settings";
import { useSettingsDomain } from "../../../graphQL/settings/scope";
import { refreshContactCategories } from "../../../components/component-contact/useContactCategories";
import VisibleFeaturesTab from "./VisibleFeaturesTab";
import pkg from "../../../../package.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_VERSION_URL = BACKEND_URL.replace(/\/graphql\/?$/, "") + "/api/version";

/* ── helpers ── */

function valueFromLabel(label) {
  return (label || "").toLowerCase().replace(/\s+/g, "_");
}

function normalizeCategoryValue(value, label = "") {
  const raw = String(value || "").trim();
  if (raw) return raw.toLowerCase().replace(/\s+/g, "_");
  return valueFromLabel(label);
}

let _idCounter = 0;
function makeId(prefix = "id") {
  return `${prefix}_${Date.now().toString(36)}_${(++_idCounter).toString(36)}`;
}

function createCategoryRow() {
  return { id: makeId("cat"), label: "", value: "", bgColor: "#E3F2FD", textColor: "#0D47A1", isActive: true };
}

function ensureBuyerCategory(rows = []) {
  const hasBuyer = rows.some((r) => {
    const label = String(r?.label || r?.name || "").trim().toLowerCase();
    const value = String(r?.value || "").trim().toLowerCase();
    return label === "buyer" || value === "buyer";
  });
  if (hasBuyer) return rows;
  return [
    ...rows,
    {
      id: makeId("cat"),
      label: "Buyer",
      value: "buyer",
      bgColor: "#DBEAFE",
      textColor: "#1E40AF",
      isActive: true,
    },
  ];
}

function createStatusRow() {
  return { id: makeId("st"), label: "", value: "", bgColor: "#E3F2FD", textColor: "#0D47A1", borderColor: "#64B5F6", isActive: true };
}

function extractDataArray(setting) {
  const payload = setting?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function pickLatest(settings) {
  if (!Array.isArray(settings) || settings.length === 0) return null;
  const ts = (v) => { const p = Date.parse(String(v ?? "")); return Number.isFinite(p) ? p : 0; };
  return settings.reduce((a, b) => (ts(b?.updatedAt ?? b?.createdAt) >= ts(a?.updatedAt ?? a?.createdAt) ? b : a), settings[0]);
}

/* ── Color swatch + picker ── */

const ColorSwatch = ({ color, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      width: 24, height: 24, borderRadius: "4px", backgroundColor: color,
      border: "1px solid #D1D5DB", cursor: "pointer",
      "&:hover": { boxShadow: "0 0 0 2px #93C5FD" },
    }}
  />
);

const ColorPickerPopover = ({ anchorEl, color, onChange, onClose }) => (
  <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
      <input type="color" value={color} onChange={(e) => onChange(e.target.value)} style={{ width: 100, height: 36, border: "none", cursor: "pointer" }} />
      <TextField size="small" value={color} onChange={(e) => onChange(e.target.value)} sx={{ width: 100, "& .MuiInputBase-root": { fontSize: 12, height: 28 } }} />
    </Box>
  </Popover>
);

/* ── System tab: UI + Backend versions ── */
function SystemTab() {
  const [backend, setBackend] = useState({ loading: true, error: "", info: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(BACKEND_VERSION_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const info = await res.json();
        if (!cancelled) setBackend({ loading: false, error: "", info });
      } catch (e) {
        if (!cancelled) setBackend({ loading: false, error: e.message || String(e), info: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const Row = ({ label, value }) => (
    <Box sx={{ display: "flex", py: 0.75, borderBottom: "1px solid #F1F5F9" }}>
      <Typography sx={{ flex: "0 0 140px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: "#0F172A", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}>{value}</Typography>
    </Box>
  );

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 1 }}>UI</Typography>
      <Row label="App" value={pkg.name || "—"} />
      <Row label="Version" value={pkg.version || "—"} />
      <Row label="Node env" value={process.env.NODE_ENV || "—"} />
      <Row label="Backend URL" value={BACKEND_URL || "—"} />

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mt: 2.5, mb: 1 }}>Backend API</Typography>
      {backend.loading ? (
        <Typography sx={{ fontSize: 12, color: "#64748B" }}>Loading {BACKEND_VERSION_URL} …</Typography>
      ) : backend.error ? (
        <Typography sx={{ fontSize: 12, color: "#DC2626" }}>Error: {backend.error}</Typography>
      ) : (
        <>
          <Row label="Version" value={backend.info?.version || "—"} />
          <Row label="Build time" value={backend.info?.buildTime || "—"} />
          <Row label="Commit" value={backend.info?.commitHash || "—"} />
          <Row label="Commit date" value={backend.info?.commitDate || "—"} />
        </>
      )}
    </Box>
  );
}

/* ── Settings table (shared by both tabs) ── */

const SettingsTable = ({ rows, onUpdate, onToggle, onDelete, onAdd, onColorOpen, columns, onSave, showSave }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "calc(80vh - 160px)" }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow sx={{ "& th": { backgroundColor: "#F5F5F5", py: 0.5 } }}>
          <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 140 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" onClick={onAdd} sx={{ width: 16, height: 16, p: 0, backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}>
                <AddIcon sx={{ fontSize: 12, color: "#FFFFFF" }} />
              </IconButton>
              <Box>Preview</Box>
            </Box>
          </TableCell>
          <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Label</TableCell>
          <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 60 }}>Active</TableCell>
          {columns.map((col) => (
            <TableCell key={col.key} sx={{ fontWeight: 600, fontSize: 12, width: 70 }}>{col.label}</TableCell>
          ))}
          <TableCell sx={{ fontWeight: 600, fontSize: 12, width: 76, textAlign: "right", pr: 0.5 }}>
            {showSave && onSave ? (
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon sx={{ fontSize: 12 }} />}
                onClick={onSave}
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 0.75,
                  height: 22,
                  fontSize: 11,
                  lineHeight: 1,
                  fontWeight: 600,
                  backgroundColor: "#16a34a",
                  "&:hover": { backgroundColor: "#15803d" },
                }}
              >
                Save
              </Button>
            ) : null}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={row.id || idx} sx={{ "& td": { py: 0.25 } }}>
            <TableCell>
              {row.label ? (
                <Chip size="small" label={row.label} sx={{ height: 22, fontSize: 11, fontWeight: 500, backgroundColor: row.bgColor, color: row.textColor, border: row.borderColor ? `1px solid ${row.borderColor}` : undefined }} />
              ) : (
                <Typography sx={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic" }}>—</Typography>
              )}
            </TableCell>
            <TableCell>
              <TextField size="small" value={row.label} onChange={(e) => onUpdate(idx, "label", e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: 12, height: 28 } }} />
            </TableCell>
            <TableCell>
              <Switch size="small" checked={row.isActive} onChange={() => onToggle(idx)} />
            </TableCell>
            {columns.map((col) => (
              <TableCell key={col.key}>
                <ColorSwatch color={row[col.key]} onClick={(e) => onColorOpen(idx, col.key, e)} />
              </TableCell>
            ))}
            <TableCell>
              <IconButton size="small" onClick={() => onDelete(idx)} sx={{ color: "#9CA3AF", "&:hover": { color: "#6B7280", backgroundColor: "#F3F4F6" } }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={4 + columns.length} align="center">
              <Typography sx={{ fontSize: 12, color: "#9CA3AF", py: 2 }}>No items yet. Click + to add one.</Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

/* ── Main dialog ── */

const CATEGORY_COLS = [
  { key: "bgColor", label: "BG" },
  { key: "textColor", label: "Text" },
];
const STATUS_COLS = [
  { key: "bgColor", label: "BG" },
  { key: "textColor", label: "Text" },
];

export default function ContactSettingsDialog({ open, onClose, authorId }) {
  // Taxonomy settings (categories, statuses, campaign types) are scoped per
  // domain; authorId is only used for the per-user Visible Features tab.
  const domain = useSettingsDomain();
  const [activeTab, setActiveTab] = useState(0);
  const [categoryRows, setCategoryRows] = useState([]);
  const [propertyCategoryRows, setPropertyCategoryRows] = useState([]);
  const [statusRows, setStatusRows] = useState([]);
  const [propertyStatusRows, setPropertyStatusRows] = useState([]);
  const [categorySetting, setCategorySetting] = useState(null);
  const [propertyCategorySetting, setPropertyCategorySetting] = useState(null);
  const [statusSetting, setStatusSetting] = useState(null);
  const [propertyStatusSetting, setPropertyStatusSetting] = useState(null);
  const [campaignTypeSetting, setCampaignTypeSetting] = useState(null);
  const [campaignTypeRows, setCampaignTypeRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [colorTarget, setColorTarget] = useState(null);

  /* ── load settings ── */

  const loadSettings = useCallback(async () => {
    if (!domain) return;
    setLoading(true);
    try {
      const [catRes, propCatRes, statusRes, propStatusRes, campTypeRes] = await Promise.all([
        apiGetSettings({ type: "addressbook", schemaType: "category", domain }),
        apiGetSettings({ type: "property", schemaType: "category", domain }),
        apiGetSettings({ type: "addressbook", schemaType: "status", domain }),
        apiGetSettings({ type: "property", schemaType: "status", domain }),
        apiGetSettings({ type: "addressbook", schemaType: "campaignType", domain }),
      ]);

      const catSetting = pickLatest(catRes?.settings);
      const propCatSetting = pickLatest(propCatRes?.settings);
      const stSetting = pickLatest(statusRes?.settings);
      const propStSetting = pickLatest(propStatusRes?.settings);
      const campTypeSetting = pickLatest(campTypeRes?.settings);
      setCategorySetting(catSetting);
      setPropertyCategorySetting(propCatSetting);
      setStatusSetting(stSetting);
      setPropertyStatusSetting(propStSetting);
      setCampaignTypeSetting(campTypeSetting);

      const mapCat = (d) => ({
        id: d.id || makeId("cat"),
        label: d.name || d.label || "",
        value: d.value || valueFromLabel(d.name || d.label || ""),
        bgColor: d?.style?.bgColor || d.bgColor || "#E3F2FD",
        textColor: d?.style?.textColor || d.textColor || "#0D47A1",
        isActive: d?.style?.isActive ?? d.isActive ?? true,
      });
      const mapSt = (d) => ({
        id: d.id || makeId("st"),
        label: d.name || d.label || "",
        value: d.value || valueFromLabel(d.name || d.label || ""),
        bgColor: d?.style?.bgColor || d.bgColor || "#E3F2FD",
        textColor: d?.style?.textColor || d.textColor || "#0D47A1",
        borderColor: d?.style?.borderColor || d.borderColor || "#64B5F6",
        isActive: d?.style?.isActive ?? d.isActive ?? true,
      });

      setCategoryRows(ensureBuyerCategory(extractDataArray(catSetting).map(mapCat)));
      setPropertyCategoryRows(extractDataArray(propCatSetting).map(mapCat));
      setStatusRows(extractDataArray(stSetting).map(mapSt));
      setPropertyStatusRows(extractDataArray(propStSetting).map(mapSt));
      setCampaignTypeRows(extractDataArray(campTypeSetting).map(mapCat));
      setDirty(false);
    } catch (e) {
      console.error("Failed to load settings:", e);
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    if (open) loadSettings();
  }, [open, loadSettings]);

  /* ── save ── */

  const handleSave = async () => {
    try {
      const catData = categoryRows.map((r) => ({
        id: r.id,
        name: r.label,
        value: normalizeCategoryValue(r.value, r.label),
        bgColor: r.bgColor,
        textColor: r.textColor,
        isActive: r.isActive,
      }));
      const propCatData = propertyCategoryRows.map((r) => ({
        id: r.id,
        name: r.label,
        value: normalizeCategoryValue(r.value, r.label),
        bgColor: r.bgColor,
        textColor: r.textColor,
        isActive: r.isActive,
      }));
      const stData = statusRows.map((r) => ({ id: r.id, name: r.label, value: r.value, bgColor: r.bgColor, textColor: r.textColor, borderColor: r.borderColor, isActive: r.isActive }));
      const propStData = propertyStatusRows.map((r) => ({ id: r.id, name: r.label, value: r.value, bgColor: r.bgColor, textColor: r.textColor, borderColor: r.borderColor, isActive: r.isActive }));
      const campTypeData = campaignTypeRows.map((r) => ({
        id: r.id,
        name: r.label,
        value: normalizeCategoryValue(r.value, r.label),
        bgColor: r.bgColor,
        textColor: r.textColor,
        isActive: r.isActive,
      }));

      const saveSetting = async (existing, schemaType, type, data) => {
        const isSynthetic = typeof existing?.id === "string" && existing.id.startsWith("default_");
        if (existing && !isSynthetic) {
          await apiUpdateSetting({ id: existing.id, schemaType, type, domain, data });
        } else {
          await apiCreateSetting({ schemaType, type, domain, data });
        }
      };

      await Promise.all([
        saveSetting(categorySetting, "category", "addressbook", catData),
        saveSetting(propertyCategorySetting, "category", "property", propCatData),
        saveSetting(statusSetting, "status", "addressbook", stData),
        saveSetting(propertyStatusSetting, "status", "property", propStData),
        saveSetting(campaignTypeSetting, "campaignType", "addressbook", campTypeData),
      ]);

      // Push the saved category list to every mounted category menu.
      await refreshContactCategories();

      setDirty(false);
      onClose();
    } catch (e) {
      console.error("Failed to save settings:", e);
    }
  };

  /* ── row handlers ── */

  const TAB_BINDINGS = [
    { rows: categoryRows, setRows: setCategoryRows, kind: "category" },
    { rows: propertyCategoryRows, setRows: setPropertyCategoryRows, kind: "category" },
    { rows: statusRows, setRows: setStatusRows, kind: "status" },
    { rows: propertyStatusRows, setRows: setPropertyStatusRows, kind: "status" },
    { rows: campaignTypeRows, setRows: setCampaignTypeRows, kind: "category" },
  ];
  const activeBinding = TAB_BINDINGS[activeTab] || TAB_BINDINGS[0];
  const rows = activeBinding.rows;
  const setRows = activeBinding.setRows;

  const handleUpdate = (idx, field, value) => {
    setDirty(true);
    setRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      if (field === "label") copy[idx].value = valueFromLabel(value);
      return copy;
    });
  };

  const handleToggle = (idx) => {
    setDirty(true);
    setRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], isActive: !copy[idx].isActive };
      return copy;
    });
  };

  const handleDelete = (idx) => {
    setDirty(true);
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAdd = () => {
    setDirty(true);
    setRows((prev) => [...prev, activeBinding.kind === "category" ? createCategoryRow() : createStatusRow()]);
  };

  const handleColorOpen = (idx, key, e) => {
    setColorTarget({ idx, key });
    setColorAnchor(e.currentTarget);
  };

  const handleColorChange = (value) => {
    if (!colorTarget) return;
    handleUpdate(colorTarget.idx, colorTarget.key, value);
  };

  const handleColorClose = () => {
    setColorTarget(null);
    setColorAnchor(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: "min(95vw, 1280px)",
          maxWidth: "95vw",
          height: "min(90vh, 900px)",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header with tabs */}
      <Box sx={{ px: 2, py: 0.5, borderBottom: "1px solid #E5E7EB", backgroundColor: "#F9FAFB" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <SettingsIcon sx={{ fontSize: 18, color: "#6B7280", flexShrink: 0 }} />
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, fontSize: 12, textTransform: "none", fontWeight: 600, py: 0, whiteSpace: "nowrap" },
            }}
          >
            <Tab label="Contact Type" />
            <Tab label="Property Category" />
            <Tab label="Contact Status" />
            <Tab label="Property Status" />
            <Tab label="Campaign Type" />
            <Tab label="Visible Features" />
            <Tab label="System" />
          </Tabs>
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, flex: 1, minHeight: 0, overflow: "auto" }}>
        {activeTab === 6 ? (
          <SystemTab />
        ) : activeTab === 5 ? (
          <VisibleFeaturesTab authorId={authorId} />
        ) : loading ? (
          <Typography sx={{ textAlign: "center", py: 4, fontSize: 13, color: "text.secondary" }}>Loading settings...</Typography>
        ) : (
          <SettingsTable
            rows={rows}
            onUpdate={handleUpdate}
            onToggle={handleToggle}
            onDelete={handleDelete}
            onAdd={handleAdd}
            onColorOpen={handleColorOpen}
            columns={activeBinding.kind === "category" ? CATEGORY_COLS : STATUS_COLS}
            onSave={handleSave}
            showSave={dirty && activeTab !== 4}
          />
        )}
      </Box>

      <ColorPickerPopover
        anchorEl={colorAnchor}
        color={colorTarget ? rows[colorTarget.idx]?.[colorTarget.key] || "#E3F2FD" : "#E3F2FD"}
        onChange={handleColorChange}
        onClose={handleColorClose}
      />
    </Dialog>
  );
}
